/**
 * @file categories.store.ts
 *
 * Zustand store for the Categories tab. Holds the full category list (the tree
 * and pagination are derived client-side from it) and the delete action; the
 * create/edit form lifecycle (`current`/`loadOne`/`submitCreate`/`submitUpdate`/
 * `resetForm`) is composed from the shared {@link createFormSlice}, wired to the
 * categories API with a cache-first lookup over the loaded list.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchCategories as apiFetchCategories,
  fetchCategoryById as apiFetchCategoryById,
  createCategory as apiCreateCategory,
  updateCategory as apiUpdateCategory,
  deleteCategory as apiDeleteCategory,
} from '../api/categories';
import type { ShopCategory, ShopCategoryPayload } from '../api/shop-types';
import { createFormSlice, toErrorCode, toMessage, type FormSlice } from './store-utils';

/**
 * Shape of the categories store: the full list plus the shared {@link FormSlice}
 * that seeds and persists the create/edit form.
 */
interface CategoriesState extends FormSlice<ShopCategory, ShopCategoryPayload, string> {
  categories: ShopCategory[];
  loading: boolean;
  error: string | null;

  /** Fetches the full category list, replacing the current one. */
  fetchCategories: () => Promise<void>;

  /**
   * Permanently deletes a category.
   *
   * @param id - The category id to delete.
   *
   * @returns `true` on success, `false` when the request fails.
   */
  deleteCategory: (id: string) => Promise<boolean>;
}

export const useCategoriesStore = create<CategoriesState>()(
  devtools(
    (set, get) => ({
      categories: [],
      loading: false,
      error: null,

      ...createFormSlice<CategoriesState, ShopCategory, ShopCategoryPayload, ShopCategory, string>({
        name: 'categories',
        fetchById: apiFetchCategoryById,
        create: apiCreateCategory,
        update: apiUpdateCategory,
        selectCached: (get) => (id) => get().categories.find((c) => c.id === id),
      })(set, get),

      fetchCategories: async () => {
        set({ loading: true, error: null }, false, 'categories/fetch/pending');
        try {
          const categories = await apiFetchCategories();
          set({ categories, loading: false }, false, 'categories/fetch/fulfilled');
        } catch (err) {
          set({ loading: false, error: toMessage(err) }, false, 'categories/fetch/rejected');
        }
      },

      deleteCategory: async (id) => {
        try {
          await apiDeleteCategory(id);
          return true;
        } catch (err) {
          // Record the code too so the view can surface a "has children" failure.
          set(
            { error: toMessage(err), formErrorCode: toErrorCode(err) },
            false,
            'categories/delete/rejected',
          );
          return false;
        }
      },
    }),
    { name: 'shop/categories' },
  ),
);
